import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useFDClients } from '../hooks/useFoodDistribution';
import { useDebouncedValue } from '../hooks/useDebounce';
import { FoodDistributionQRCode } from '../components/FoodDistributionQRCode';
import '../styles/FoodDistribution.css';

type SortKey = 'name' | 'zipCode' | 'householdSize' | 'createdAt';

export function FoodDistributionPage() {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [showQR, setShowQR] = useState(false);
  const [sortBy, setSortBy] = useState<SortKey>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [zipFilter, setZipFilter] = useState('');
  const pageSize = 20;

  const debouncedSearch = useDebouncedValue(search, 400);
  const debouncedZip = useDebouncedValue(zipFilter, 400);

  const { data, isLoading, error } = useFDClients({
    searchQuery: debouncedSearch || undefined,
    isActive: true,
    page,
    pageSize,
    sortBy,
    sortDir,
    zipCode: debouncedZip || undefined,
  });

  const clients = data?.data ?? [];
  const pagination = data?.pagination;

  const handleSearch = (val: string) => {
    setSearch(val);
    setPage(1);
  };

  const handleSort = (col: SortKey) => {
    if (sortBy === col) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(col);
      setSortDir('asc');
    }
    setPage(1);
  };

  const sortIndicator = (col: SortKey) => {
    if (sortBy !== col) {
      return <span style={{ color: '#ccc', marginLeft: 4 }}>↕</span>;
    }
    return <span style={{ marginLeft: 4 }}>{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  return (
    <div>
      <div className="page-header">
        <h2>Food Distribution</h2>
        <p>Client check-in records, visit history, and referrals</p>
      </div>

      {/* Quick nav */}
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
        <Link to="/food-distribution/referrals">
          <button className="button-secondary">Referrals</button>
        </Link>
        <Link to="/food-distribution/reports">
          <button className="button-secondary">Reports</button>
        </Link>
        <button className="button-secondary" onClick={() => setShowQR(!showQR)}>
          {showQR ? 'Hide QR Code' : 'QR Code Poster'}
        </button>
      </div>

      {showQR && (
        <div style={{ marginBottom: '1.5rem' }}>
          <FoodDistributionQRCode />
        </div>
      )}

      {/* Client list */}
      <div className="card">
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1rem',
            flexWrap: 'wrap',
            gap: '0.75rem',
          }}
        >
          <h3 style={{ margin: 0 }}>Clients</h3>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <input
              type="search"
              placeholder="Filter by zip code..."
              value={zipFilter}
              onChange={(e) => {
                setZipFilter(e.target.value);
                setPage(1);
              }}
              style={{
                padding: '0.5rem 0.875rem',
                border: '1px solid #ddd',
                borderRadius: 6,
                fontSize: '0.9rem',
                width: 160,
                fontFamily: 'Montserrat, sans-serif',
              }}
            />
            <input
              type="search"
              placeholder="Search by name or phone..."
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
              style={{
                padding: '0.5rem 0.875rem',
                border: '1px solid #ddd',
                borderRadius: 6,
                fontSize: '0.9rem',
                width: 240,
                fontFamily: 'Montserrat, sans-serif',
              }}
            />
          </div>
        </div>

        {isLoading && <p style={{ color: '#888' }}>Loading...</p>}
        {error && <p style={{ color: '#c62828' }}>Failed to load clients. Please try again.</p>}

        {!isLoading && !error && clients.length === 0 && (
          <div className="fd-empty">
            <div className="fd-empty-icon">🥫</div>
            <p>
              {debouncedSearch
                ? 'No clients match your search.'
                : 'No food distribution clients yet.'}
            </p>
          </div>
        )}

        {!isLoading && clients.length > 0 && (
          <>
            <div className="fd-table-wrap">
              <table className="fd-table">
                <thead>
                  <tr>
                    <th>UID</th>
                    <th
                      onClick={() => handleSort('name')}
                      style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                    >
                      Name{sortIndicator('name')}
                    </th>
                    <th>Phone</th>
                    <th
                      onClick={() => handleSort('zipCode')}
                      style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                    >
                      Zip{sortIndicator('zipCode')}
                    </th>
                    <th
                      onClick={() => handleSort('householdSize')}
                      style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                    >
                      Household{sortIndicator('householdSize')}
                    </th>
                    <th
                      onClick={() => handleSort('createdAt')}
                      style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                    >
                      Created{sortIndicator('createdAt')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {clients.map((c) => (
                    <tr key={c.fdClientId}>
                      <td>
                        <Link to={`/food-distribution/${c.fdClientId}`} className="fd-table-link">
                          {c.clientUid}
                        </Link>
                      </td>
                      <td>
                        <Link to={`/food-distribution/${c.fdClientId}`} className="fd-table-link">
                          {c.firstName} {c.lastName}
                        </Link>
                      </td>
                      <td>{c.phone}</td>
                      <td>{c.zipCode}</td>
                      <td>
                        {c.householdSize} ({c.childrenInHousehold} ch.)
                      </td>
                      <td>{c.createdAt ? new Date(c.createdAt).toLocaleDateString() : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {pagination && (
              <div className="fd-pagination">
                <span className="fd-pagination-info">
                  {pagination.totalItems} client{pagination.totalItems !== 1 ? 's' : ''}
                  {' · '}Page {pagination.page} of {pagination.totalPages}
                </span>
                <div className="fd-pagination-buttons">
                  <button onClick={() => setPage(1)} disabled={page === 1}>
                    «
                  </button>
                  <button onClick={() => setPage(page - 1)} disabled={page === 1}>
                    ‹ Prev
                  </button>
                  <button
                    onClick={() => setPage(page + 1)}
                    disabled={page >= pagination.totalPages}
                  >
                    Next ›
                  </button>
                  <button
                    onClick={() => setPage(pagination.totalPages)}
                    disabled={page >= pagination.totalPages}
                  >
                    »
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
